import { useEffect, useRef } from 'react';

const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

export const useFocusTrap = (active, onDismiss, options = {}) => {
    const containerRef = useRef(null);
    const onDismissRef = useRef(onDismiss);
    const dismissOnEscape = options.dismissOnEscape !== false;

    useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    useEffect(() => {
        if (!active) return undefined;
        const container = containerRef.current;
        const previouslyFocused = document.activeElement;

        const focusable = () => Array.from(container?.querySelectorAll(focusableSelector) || [])
            .filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');

        const focusTimer = window.setTimeout(() => {
            const first = focusable()[0] || container;
            first?.focus?.({ preventScroll: true });
        }, 0);

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && dismissOnEscape) {
                event.preventDefault();
                onDismissRef.current?.();
                return;
            }

            if (event.key !== 'Tab') return;
            const nodes = focusable();
            if (!nodes.length) {
                event.preventDefault();
                container?.focus?.({ preventScroll: true });
                return;
            }

            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener('keydown', handleKeyDown, true);
            if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
                previouslyFocused.focus({ preventScroll: true });
            }
        };
    }, [active, dismissOnEscape]);

    return containerRef;
};
